/**
 * FEATURE 1: Data Snapshot (Direct Selection) - ISOLATED LOADER
 * Completely independent data loader for Feature 1 only
 * No conflicts with other features - has its own state and data sources
 */

import { useEffect, useRef } from "react";
import Papa from "papaparse";
import { fetchR2CsvText } from "../../utils/fetchR2Csv";

// ISOLATED CACHE - Only for Feature 1
const feature1Cache = new Map();

// RegionID mapping cache for metro areas
const regionIdMapping = new Map();

/**
 * Isolated Data Loader for Feature 1: Data Snapshot (Direct Selection)
 * Handles all geographic levels independently
 */
export function useFeature1DataLoader({
  areaType,
  selectedName,
  setFeature1Data,
  setFeature1Loading,
  setFeature1Error,
  shouldLoad = true
}) {
  // ALL HOOKS MUST BE CALLED UNCONDITIONALLY
  const hasLoadedRef = useRef(false);
  const hookId = useRef(Math.random().toString(36).substr(2, 9));

  console.log(`[FEATURE1_LOADER] Hook called with areaType: ${areaType}, selectedName: ${selectedName}, shouldLoad: ${shouldLoad}`);

  // Reset when area type changes
  useEffect(() => {
    if (!shouldLoad) return; // Guard clause inside useEffect
    
    hasLoadedRef.current = false;
    console.log(`[FEATURE1_LOADER] Reset for areaType: ${areaType}`);
  }, [areaType, shouldLoad]);

  // Load data when area type and selection are available
  useEffect(() => {
    console.log(`[FEATURE1_LOADER] useEffect triggered - areaType: ${areaType}, hasLoaded: ${hasLoadedRef.current}, shouldLoad: ${shouldLoad}`);
    
    if (!shouldLoad) {
      console.log(`[FEATURE1_LOADER] Skipping load - shouldLoad is false`);
      return;
    }
    
    if (!areaType) {
      console.log(`[FEATURE1_LOADER] Skipping load - areaType is empty: ${areaType}`);
      return;
    }
    
    if (hasLoadedRef.current) {
      console.log(`[FEATURE1_LOADER] Already loaded - skipping reload for ${areaType}`);
      return;
    }

    console.log(`[FEATURE1_LOADER] Starting data load for ${areaType}`);
    hasLoadedRef.current = true;
    setFeature1Loading(true);
    setFeature1Error(null);

    const loadFeature1Data = async () => {
      try {
        console.log(`[FEATURE1_LOADER] Starting loadFeature1Data for ${areaType}`);
        const levelDir = areaType.toLowerCase();
        
        // FEATURE 1 DATA SOURCES - R2 paths
        const r2Keys = {
          // Latest data for snapshots
          latest: `public/data/Merged_Last_Month_Data/${levelDir}_5y_50k_merged.csv`,
          // Full data for charts (on-demand)
          full: `public/data/Merged_Full_Data/${levelDir}_5y_50k_merged.csv`,
          // Dropdown data for area selection
          dropdown: `public/data/Merged_No_Data/Standard/${levelDir}_5y_50k_standard_merged.csv`
        };

        // Load latest data first (for immediate display) from R2
        console.log(`[FEATURE1_LOADER] Loading latest data from R2: ${r2Keys.latest}`);
        console.log(`[FEATURE1_LOADER] ⚠️ DEBUG MODE - Will log CSV columns when loaded`);

        const text = await fetchR2CsvText(r2Keys.latest);
        
        // Parse CSV data
        Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: 'greedy',
          complete: async (result) => {
            try {
              const rows = result.data || [];
              console.log(`[FEATURE1_LOADER] Parsed ${rows.length} rows`);
              
              // DEBUG: Log CSV columns
              if (rows.length > 0) {
                const columns = Object.keys(rows[0]);
                console.log(`[FEATURE1_LOADER] CSV has ${columns.length} columns, parsed ${rows.length} rows`);
              }

              // Load RegionID mapping first (needed for chart loading)
              // If mapping fails, continue anyway (some areas might have RegionID in CSV)
              try {
                await loadRegionIdMapping(levelDir);
              } catch (mappingError) {
                console.warn(`[FEATURE1_LOADER] RegionID mapping failed (non-critical):`, mappingError);
                // Continue processing - RegionID might be in CSV data itself
              }
              
              // Process data for Feature 1
              const processedData = processFeature1Data(rows, areaType, levelDir);
              
              // Update Feature 1 state
              setFeature1Data(processedData);
              setFeature1Loading(false);
              
              console.log(`[FEATURE1_LOADER] Feature 1 data loaded successfully`);
            } catch (error) {
              console.error(`[FEATURE1_LOADER] Error processing data:`, error);
              setFeature1Error(`Data processing error: ${error.message}`);
              setFeature1Loading(false);
            }
          },
          error: (error) => {
            console.error(`[FEATURE1_LOADER] CSV parsing error:`, error);
            setFeature1Error(`CSV parsing error: ${error.message}`);
            setFeature1Loading(false);
          }
        });

      } catch (error) {
        console.error(`[FEATURE1_LOADER] Load error:`, error);
        setFeature1Error(`Load error: ${error.message}`);
        setFeature1Loading(false);
      }
    };

    loadFeature1Data();

  }, [areaType, shouldLoad, setFeature1Data, setFeature1Loading, setFeature1Error]);

  // Disable automatic name-based chart loading: Feature 1 is RegionID-only for charts
  useEffect(() => {
    console.log(`[FEATURE1_LOADER] Auto chart loading disabled (RegionID-only).`);
  }, [areaType, selectedName, shouldLoad]);

  // Load RegionID mapping for metro, state, county, city, and zipcode areas
  const loadRegionIdMapping = async (areaType = 'metro') => {
    const cacheKey = `${areaType}_mapping`;
    if (regionIdMapping.has(cacheKey)) {
      console.log(`[FEATURE1_LOADER] RegionID mapping already loaded for ${areaType}: ${regionIdMapping.get(cacheKey).size} entries`);
      return;
    }

    try {
      console.log(`[FEATURE1_LOADER] Loading RegionID mapping for ${areaType} from R2`);
      const r2Key = `public/data/Merged_No_Data/Standard/${areaType}_5y_50k_standard_merged.csv`;
      const text = await fetchR2CsvText(r2Key);
      
      // Wrap Papa.parse in a Promise to make it awaitable
      return new Promise((resolve, reject) => {
        Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: 'greedy',
          complete: (result) => {
            try {
              console.log(`[FEATURE1_LOADER] RegionID mapping CSV parsed for ${areaType}, rows: ${result.data.length}`);
              const mapping = new Map();
              result.data.forEach(row => {
                let nameKey;
                if (areaType === 'metro') {
                  nameKey = 'Metro';
                } else if (areaType === 'state') {
                  nameKey = 'State';
                } else if (areaType === 'county') {
                  nameKey = 'CountyName';
                } else if (areaType === 'city') {
                  nameKey = 'City';
                } else if (areaType === 'zipcode') {
                  nameKey = 'Zipcode';
                } else if (areaType === 'neighborhood') {
                  nameKey = 'Neighborhood';
                }
                
                if (row[nameKey] && row.RegionID) {
                  mapping.set(row[nameKey], row.RegionID);
                }
              });
              regionIdMapping.set(cacheKey, mapping);
              console.log(`[FEATURE1_LOADER] RegionID mapping loaded for ${areaType}: ${mapping.size} entries`);
              resolve(mapping);
            } catch (error) {
              reject(error);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
      });
    } catch (error) {
      console.error(`[FEATURE1_LOADER] Error loading RegionID mapping for ${areaType}:`, error);
      throw error;
    }
  };

  // Load chart data on demand
  const loadChartData = async (selectedName, metric = 'Price', regionId = null) => {
    console.log(`[FEATURE1_LOADER] loadChartData called with selectedName: ${selectedName}, metric: ${metric}, regionId: ${regionId}`);
    console.log(`[FEATURE1_LOADER] Current areaType: ${areaType}, shouldLoad: ${shouldLoad}`);
    
    if (!areaType || !selectedName) {
      console.log(`[FEATURE1_LOADER] Missing requirements - areaType: ${areaType}, selectedName: ${selectedName}`);
      return;
    }
    if (regionId == null) {
      console.error(`[FEATURE1_LOADER] RegionID is required for chart loading (RegionID-only). Skipping.`);
      return;
    }

    try {
      const levelDir = areaType.toLowerCase();
      
      // For all levels, use the merged files from R2 Pre-Standard
      let r2Key;
      if (['metro', 'state', 'county', 'city', 'zipcode', 'neighborhood'].includes(levelDir)) {
        r2Key = `public/data/Merged_Full_Data/${levelDir}_5y_50k_merged.csv`;
        console.log(`[FEATURE1_LOADER] Using R2 merged file for ${levelDir}: ${r2Key}`);
      } else {
        // Fallback for other levels, use metric-specific files
        const metricFileMap = {
          'Price': 'Price',
          'Rent': 'Rent', 
          'Inventory': 'Inventory',
          'DOM': 'DOM'
        };
        
        const metricFileName = metricFileMap[metric] || 'Price';
        r2Key = `public/data/${metricFileName}/${levelDir}_${metricFileName.toLowerCase()}_5y_50k.csv`;
        console.log(`[FEATURE1_LOADER] Using R2 metric-specific file: ${r2Key}`);
      }
      
      console.log(`[FEATURE1_LOADER] Loading chart data for ${selectedName}, metric: ${metric} from R2: ${r2Key}`);
      
      const text = await fetchR2CsvText(r2Key);
      console.log(`[FEATURE1_LOADER] CSV data loaded, length: ${text.length} characters`);
      
      Papa.parse(text, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: 'greedy',
        complete: (result) => {
          console.log(`[FEATURE1_LOADER] CSV parsed, rows: ${result.data.length}`);
          
          // Debug: Check if metric field exists in CSV
          if (result.data.length > 0) {
            const sampleRow = result.data[0];
            const fieldName = metric === 'DOM' ? 'Days' : metric;
            const hasField = sampleRow.hasOwnProperty(fieldName);
            console.log(`[FEATURE1_LOADER] Checking for metric field "${fieldName}" in CSV:`, {
              hasField,
              availableFields: Object.keys(sampleRow).filter(k => k.toLowerCase().includes(metric.toLowerCase())),
              sampleValue: sampleRow[fieldName],
              sampleRowKeys: Object.keys(sampleRow).slice(0, 20)
            });
          }
          
          const chartData = processChartData(result.data, selectedName, areaType, metric, regionId);
          console.log(`[FEATURE1_LOADER] Chart data processed:`, chartData.length, 'points');
          if (chartData.length > 0) {
            console.log(`[FEATURE1_LOADER] Sample chart data:`, chartData.slice(0, 3));
            console.log(`[FEATURE1_LOADER] Chart data value range:`, {
              min: Math.min(...chartData.map(d => d.price)),
              max: Math.max(...chartData.map(d => d.price))
            });
          } else {
            console.warn(`[FEATURE1_LOADER] ⚠️ No chart data points returned for ${metric}!`);
            console.warn(`[FEATURE1_LOADER] ⚠️ Check console for [PROCESS_CHART_DATA] logs above to see why`);
            console.warn(`[FEATURE1_LOADER] ⚠️ Possible causes:`);
            console.warn(`[FEATURE1_LOADER]   1. RegionID ${regionId} not found in CSV file`);
            console.warn(`[FEATURE1_LOADER]   2. Metric field "${metric === 'DOM' ? 'Days' : metric}" missing or all null in CSV`);
            console.warn(`[FEATURE1_LOADER]   3. Name mismatch: looking for "${selectedName}"`);
          }
          setFeature1Data(prev => ({ ...prev, chartData }));
          console.log(`[FEATURE1_LOADER] Chart data loaded for ${selectedName}, metric: ${metric}:`, chartData.length, 'points');
        }
      });

    } catch (error) {
      console.error(`[FEATURE1_LOADER] Chart data error:`, error);
    }
  };

  // Return Feature 1 specific utilities
  return {
    reloadFeature1: () => {
      hasLoadedRef.current = false;
      setFeature1Loading(true);
    },
    clearFeature1Cache: () => {
      feature1Cache.clear();
    },
    loadChartData,
    getFeature1CacheStats: () => ({
      cacheSize: feature1Cache.size,
      hookId: hookId.current
    })
  };
}

/**
 * Process data specifically for Feature 1
 */
function processFeature1Data(rows, areaType, levelDir = null) {
  const nameKey = getNameKeyByAreaType(areaType);
  const actualLevelDir = levelDir || areaType.toLowerCase();
  
  // Convert to wide format if needed
  const formatted = convertToWideFormat(rows, nameKey);
  
  // Get RegionID mapping if available
  const cacheKey = `${actualLevelDir}_mapping`;
  const regionIdMap = regionIdMapping.get(cacheKey);
  
  // Extract latest data with proper field mapping
  const latestByMetro = {};
  const allDates = new Set();
  
  formatted.forEach(row => {
    if (row.Date) allDates.add(row.Date);
    const rawAreaName = row[nameKey] 
      ?? row.CountyName 
      ?? row.County 
      ?? row.AreaName 
      ?? row.Metro 
      ?? row.City 
      ?? row.Zipcode 
      ?? row.Neighborhood;

    if (rawAreaName) {
      const areaName = typeof rawAreaName === 'string' ? rawAreaName.trim() : rawAreaName;
      
      // Try to get RegionID from row first, then from mapping
      let regionId = row.RegionID || row.regionId || row.region_id;
      if (!regionId && regionIdMap) {
        regionId = regionIdMap.get(areaName);
      }
      
      // Map raw CSV fields to normalized field names expected by UI components
      const processedRow = {
        ...row, // Keep all original fields
        // Ensure RegionID is set (critical for chart loading)
        RegionID: regionId || row.RegionID || row.regionId || row.region_id,
        regionId: regionId || row.RegionID || row.regionId || row.region_id,
        region_id: regionId || row.RegionID || row.regionId || row.region_id,
        // Price fields
        PriceDelta: row.Price_delta,
        PriceYoY: row.Price_YoY_delta,
        PriceYoYDelta: row.Price_YoY_delta,
        // Rent fields  
        RentDelta: row.Rent_delta,
        RentYoY: row.Rent_YoY_delta || row.rent_12m_delta,
        RentYoYDelta: row.Rent_YoY_delta || row.rent_12m_delta,
        // Inventory fields
        InventoryDelta: row.Inventory_delta,
        InventoryYoY: row.Inventory_YoY,
        // Days fields
        DaysDelta: row.Days_delta ?? row.DOM_delta,
        // Forecast fields
        Fcst1m: row.f1m,
        Fcst3m: row.f3m,
        Fcst12m: row.f12m,
        // Alternative forecast field names
        f1m: row.f1m,
        f3m: row.f3m,
        f12m: row.f12m
      };
      
      latestByMetro[areaName] = processedRow;
    }
  });

  // Get latest date
  const latestDate = Array.from(allDates).sort().pop() || "";

  console.log(`[FEATURE1_LOADER] Processed ${Object.keys(latestByMetro).length} areas with RegionID mapping`);
  if (regionIdMap) {
    const areasWithRegionId = Object.values(latestByMetro).filter(area => area.RegionID).length;
    console.log(`[FEATURE1_LOADER] ${areasWithRegionId} areas have RegionID`);
  }

  return {
    allDates: Array.from(allDates).sort(),
    latestByMetro,
    scores: null, // Feature 1 doesn't use scores
    ranks: null,  // Feature 1 doesn't use ranks
    latestDate,
    chartData: []
  };
}

/**
 * Helper function to pad zipcode to 5 digits
 */
function padZip5(zip) {
  return String(zip ?? "").padStart(5, "0");
}

/**
 * Process chart data for Feature 1
 */
function processChartData(rows, selectedName, areaType, metric = 'Price', regionId = null) {
  console.log(`[PROCESS_CHART_DATA] ===== Processing chart data for ${selectedName} =====`);
  console.log(`[PROCESS_CHART_DATA] Total rows in file:`, rows.length);
  console.log(`[PROCESS_CHART_DATA] Metric: ${metric}, RegionID: ${regionId}, AreaType: ${areaType}`);
  
  const nameKey = getNameKeyByAreaType(areaType);
  console.log(`[PROCESS_CHART_DATA] Using nameKey: ${nameKey}`);
  
  // Debug: Check what RegionIDs and metro names exist in the file
  if (rows.length > 0) {
    const sampleMetros = new Set();
    const regionIds = new Set();
    rows.slice(0, 100).forEach(row => {
      if (row[nameKey]) sampleMetros.add(row[nameKey]);
      if (row.RegionID) regionIds.add(row.RegionID);
    });
    console.log(`[PROCESS_CHART_DATA] Sample metros in file (first 100 rows):`, Array.from(sampleMetros).slice(0, 10));
    console.log(`[PROCESS_CHART_DATA] Sample RegionIDs in file (first 100 rows):`, Array.from(regionIds).slice(0, 10));
    
    // Specifically check for New York
    if (selectedName && selectedName.toLowerCase().includes('new york')) {
      const nyRows = rows.filter(row => {
        const metroName = row[nameKey];
        return metroName && (
          metroName.toLowerCase().includes('new york') ||
          metroName.toLowerCase() === 'new york, ny'
        );
      });
      console.log(`[PROCESS_CHART_DATA] 🔍 NEW YORK DEBUG: Found ${nyRows.length} rows matching "New York"`);
      if (nyRows.length > 0) {
        const nyRegionIds = [...new Set(nyRows.map(r => r.RegionID))];
        console.log(`[PROCESS_CHART_DATA] 🔍 NEW YORK RegionIDs in file:`, nyRegionIds);
        console.log(`[PROCESS_CHART_DATA] 🔍 Searching RegionID: ${regionId}`);
        console.log(`[PROCESS_CHART_DATA] 🔍 Match found?`, nyRegionIds.includes(regionId));
      }
    }
  }
  
  // Map metric names to CSV column names
  const metricMap = {
    'Price': 'Price',
    'Rent': 'Rent',
    'Inventory': 'Inventory',
    'DOM': 'Days'
  };
  
  const fieldName = metricMap[metric] || 'Price';
  console.log(`[PROCESS_CHART_DATA] Using fieldName: ${fieldName}`);
  
  let filteredRows = [];
  console.log(`[PROCESS_CHART_DATA] Enforcing RegionID-only matching for Feature 1 charts. RegionID: ${regionId}`);
  if (regionId == null) {
    console.error(`[PROCESS_CHART_DATA] Missing RegionID for selection "${selectedName}" at ${areaType}. Returning empty series.`);
    return [];
  }
  
  // Try exact RegionID match first
  filteredRows = rows.filter(row => row.RegionID === regionId);
  console.log(`[PROCESS_CHART_DATA] RegionID exact matches found: ${filteredRows.length}`);
  
  // If no exact match, try name-based fallback (especially for New York which was added later)
  if (filteredRows.length === 0) {
    console.log(`[PROCESS_CHART_DATA] ⚠️ No RegionID match found. Trying name-based fallback...`);
    const selectedNameLower = selectedName ? selectedName.toLowerCase() : '';
    const nameMatches = rows.filter(row => {
      const metroName = row[nameKey];
      if (!metroName) return false;
      const metroNameLower = metroName.toLowerCase();
      
      // Try multiple matching strategies
      return (
        metroNameLower === selectedNameLower ||
        metroNameLower.includes(selectedNameLower) ||
        selectedNameLower.includes(metroNameLower) ||
        // For New York specifically, handle various formats
        (selectedNameLower.includes('new york') && (
          metroNameLower.includes('new york') ||
          metroNameLower.includes('nyc') ||
          metroNameLower === 'new york'
        ))
      );
    });
    console.log(`[PROCESS_CHART_DATA] Name-based matches found: ${nameMatches.length}`);
    if (nameMatches.length > 0) {
      const foundRegionId = nameMatches[0].RegionID;
      const foundName = nameMatches[0][nameKey];
      console.log(`[PROCESS_CHART_DATA] ⚠️ Found name match!`);
      console.log(`[PROCESS_CHART_DATA] ⚠️   Searching for: "${selectedName}" (RegionID: ${regionId})`);
      console.log(`[PROCESS_CHART_DATA] ⚠️   Found in file: "${foundName}" (RegionID: ${foundRegionId})`);
      console.log(`[PROCESS_CHART_DATA] ⚠️   RegionID mismatch detected - using name-based match`);
      filteredRows = nameMatches;
    } else {
      console.log(`[PROCESS_CHART_DATA] ⚠️ No name-based matches found either. Available metro names in file:`, 
        [...new Set(rows.map(r => r[nameKey]).filter(Boolean))].slice(0, 20)
      );
    }
  }
  
  if (filteredRows.length > 0) {
    console.log(`[PROCESS_CHART_DATA] Sample filtered row:`, {
      name: filteredRows[0][nameKey],
      regionId: filteredRows[0].RegionID,
      date: filteredRows[0].Date,
      value: filteredRows[0][fieldName],
      fieldName,
      hasField: filteredRows[0].hasOwnProperty(fieldName),
      allMetricFields: Object.keys(filteredRows[0]).filter(k => 
        k.toLowerCase().includes(metric.toLowerCase()) || 
        (metric === 'DOM' && k.toLowerCase().includes('days'))
      )
    });
    console.log(`[PROCESS_CHART_DATA] All columns in sample row:`, Object.keys(filteredRows[0]));
    
    // Debug: Check how many rows have valid values for this metric
    const rowsWithValue = filteredRows.filter(row => {
      const value = row[fieldName];
      return value !== null && value !== undefined && !isNaN(value) && value !== '';
    });
    console.log(`[PROCESS_CHART_DATA] Rows with valid ${fieldName} values: ${rowsWithValue.length} out of ${filteredRows.length}`);
  } else {
    console.error(`[PROCESS_CHART_DATA] ⚠️ No rows found matching RegionID ${regionId} for ${selectedName}`);
  }
  
  // Check if this is a time series file or a snapshot file
  const uniqueDates = [...new Set(rows.map(row => row.Date).filter(Boolean))];
  console.log(`[PROCESS_CHART_DATA] Unique dates in file:`, uniqueDates.length);
  console.log(`[PROCESS_CHART_DATA] Sample dates:`, uniqueDates.slice(0, 5));
  
  const result = filteredRows
    .map(row => ({
      date: row.Date,
      // Map to 'price' for chart component compatibility
      price: row[fieldName]
    }))
    .filter(row => row.price !== null && row.price !== undefined && !isNaN(row.price))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  
  console.log(`[PROCESS_CHART_DATA] Final result:`, result.length, 'points');
  console.log(`[PROCESS_CHART_DATA] Sample result:`, result.slice(0, 3));
  
  return result;
}

/**
 * Get name key by area type
 */
function getNameKeyByAreaType(areaType) {
  const nameKeyMap = {
    State: "State",
    Metro: "Metro", 
    // Latest + full merged files (used for Feature 1 snapshots/charts) expose
    // the county name in the "County" column. Dropdown data still uses
    // CountyName, but RegionID lookups handle that separately.
    County: "County",
    City: "City",
    Zipcode: "Zipcode",
    Neighborhood: "Neighborhood"
  };
  return nameKeyMap[areaType] || "Metro";
}

/**
 * Convert long format to wide format
 */
function convertToWideFormat(rows, nameKey) {
  // Simple conversion - assumes data is already in wide format
  // For more complex conversion, use the utility functions
  return rows;
}
